import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { FormControl } from '@angular/forms';
import { Subject, takeUntil, debounceTime, distinctUntilChanged, forkJoin, map, finalize } from 'rxjs';
import { DocumentTemplateService } from '../document-template.service';
import { DocumentTypesService } from '../../document-types/document-types.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { DirectieData, CompartimentData, UserData } from '../../document-types/document-types.interface';
import Swal from 'sweetalert2';
import {
    TemplateRedactionFlowData,
    TemplateRedactionNode,
    REDACTION_TYPES
} from './redaction-flow-dialog.interface';

@Component({
    selector: 'app-redaction-flow-dialog',
    templateUrl: './redaction-flow-dialog.component.html',
    styleUrls: ['./redaction-flow-dialog.component.scss']
})
export class RedactionFlowDialogComponent implements OnInit, OnDestroy {
    private unsubscribe$ = new Subject<void>();

    flowData: TemplateRedactionFlowData | null = null;
    isLoading = true;
    isSaving = false;
    errorMessage: string | null = null;

    redactionTypes = REDACTION_TYPES;

    // Permission slug
    readonly PERM_MANAGE = 'indoc-module.settings.template-documente.actualizare-redactari';

    // Data sources for dropdowns
    directiiList: DirectieData[] = [];
    functiiList: { id: number; name: string }[] = [];

    // Cache for compartimente and users
    compartimenteByDirectie: { [directieId: number]: CompartimentData[] } = {};
    usersByDirectieCompartiment: { [key: string]: UserData[] } = {};

    // Typeahead search controls (per row index)
    directiiSearchControls: { [index: number]: FormControl } = {};
    compartimenteSearchControls: { [index: number]: FormControl } = {};
    functiiSearchControls: { [index: number]: FormControl } = {};
    usersSearchControls: { [index: number]: FormControl } = {};

    // Filtered data for typeahead (per row index)
    filteredDirectiiByIndex: { [index: number]: DirectieData[] } = {};
    filteredCompartimenteByIndex: { [index: number]: CompartimentData[] } = {};
    filteredFunctiiByIndex: { [index: number]: { id: number; name: string }[] } = {};
    filteredUsersByIndex: { [index: number]: UserData[] } = {};

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            id: number;
            name: string;
        },
        private dialogRef: MatDialogRef<RedactionFlowDialogComponent>,
        private templateService: DocumentTemplateService,
        private documentTypesService: DocumentTypesService,
        public permissionsService: PermissionsService
    ) {}

    ngOnInit(): void {
        this.loadInitialData();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    loadInitialData(): void {
        this.isLoading = true;
        this.errorMessage = null;

        const redactions$ = this.templateService.getRedactionFlowData(this.data.id);
        const directii$ = this.documentTypesService.getAvailableDirectii().pipe(
            map((res: any) => res.data || [])
        );
        const functii$ = this.templateService.getFunctiiUserActiveList();

        forkJoin({ redactions$, directii$, functii$ })
            .pipe(
                takeUntil(this.unsubscribe$),
                finalize(() => {
                    this.isLoading = false;
                })
            )
            .subscribe({
                next: ({ redactions$, directii$, functii$ }) => {
                    if (redactions$ && (redactions$ as any).data) {
                        this.flowData = (redactions$ as any).data;
                    }
                    this.directiiList = directii$;
                    this.functiiList = functii$ as { id: number; name: string }[];

                    // Initialize typeahead controls for existing rows
                    setTimeout(() => {
                        this.flowData?.redactions.forEach((row, index) => {
                            this.setupTypeaheadForRow(index);

                            // Preload compartimente and users if directie is selected
                            if (row.directie_id) {
                                this.loadCompartimenteForDirectie(row.directie_id);
                                const compartimentId = row.compartiment_id ?? undefined;
                                this.loadUsersForSelection(row.directie_id, compartimentId);
                            }
                        });
                    }, 0);
                },
                error: (error) => {
                    console.error('Error loading data:', error);
                    this.errorMessage = 'Nu s-a putut incarca circuitul de redactare.';
                }
            });
    }

    closeDialog(): void {
        this.dialogRef.close();
    }

    get canManage(): boolean {
        return this.permissionsService.hasPermission(this.PERM_MANAGE);
    }

    get hasRedactions(): boolean {
        return !!(this.flowData?.redactions && this.flowData.redactions.length > 0);
    }

    // =====================================================
    // Typeahead Setup and Filtering
    // =====================================================

    setupTypeaheadForRow(index: number): void {
        // Directii search control
        if (!this.directiiSearchControls[index]) {
            this.directiiSearchControls[index] = new FormControl('');
            this.filteredDirectiiByIndex[index] = [...this.directiiList];

            this.directiiSearchControls[index].valueChanges
                .pipe(
                    takeUntil(this.unsubscribe$),
                    debounceTime(200),
                    distinctUntilChanged()
                )
                .subscribe(searchTerm => {
                    this.filteredDirectiiByIndex[index] = this.filterList(
                        this.directiiList,
                        searchTerm,
                        'name'
                    );
                });
        }

        // Compartimente search control
        if (!this.compartimenteSearchControls[index]) {
            this.compartimenteSearchControls[index] = new FormControl('');
            this.filteredCompartimenteByIndex[index] = [];

            this.compartimenteSearchControls[index].valueChanges
                .pipe(
                    takeUntil(this.unsubscribe$),
                    debounceTime(200),
                    distinctUntilChanged()
                )
                .subscribe(searchTerm => {
                    const row = this.flowData?.redactions[index];
                    const directieId = row?.directie_id;
                    if (directieId) {
                        const compartimente = this.compartimenteByDirectie[directieId] || [];
                        this.filteredCompartimenteByIndex[index] = this.filterList(
                            compartimente,
                            searchTerm,
                            'name'
                        );
                    }
                });
        }

        // Functii search control
        if (!this.functiiSearchControls[index]) {
            this.functiiSearchControls[index] = new FormControl('');
            this.filteredFunctiiByIndex[index] = [...this.functiiList];

            this.functiiSearchControls[index].valueChanges
                .pipe(
                    takeUntil(this.unsubscribe$),
                    debounceTime(200),
                    distinctUntilChanged()
                )
                .subscribe(searchTerm => {
                    this.filteredFunctiiByIndex[index] = this.filterList(
                        this.functiiList,
                        searchTerm,
                        'name'
                    );
                });
        }

        // Users search control
        if (!this.usersSearchControls[index]) {
            this.usersSearchControls[index] = new FormControl('');
            this.filteredUsersByIndex[index] = [];

            this.usersSearchControls[index].valueChanges
                .pipe(
                    takeUntil(this.unsubscribe$),
                    debounceTime(200),
                    distinctUntilChanged()
                )
                .subscribe(searchTerm => {
                    const row = this.flowData?.redactions[index];
                    const directieId = row?.directie_id;
                    const compartimentId = row?.compartiment_id ?? undefined;
                    if (directieId) {
                        const cacheKey = compartimentId ? `${directieId}-${compartimentId}` : `${directieId}`;
                        const users = this.usersByDirectieCompartiment[cacheKey] || [];
                        this.filteredUsersByIndex[index] = this.filterList(
                            users,
                            searchTerm,
                            'name'
                        );
                    }
                });
        }
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    filterList<T extends { [key: string]: any }>(list: T[], searchTerm: string, field: string): T[] {
        if (!searchTerm) return [...list];
        const normalized = this.normalizeString(searchTerm);
        return list.filter(item =>
            this.normalizeString(item[field] || '').includes(normalized)
        );
    }

    // =====================================================
    // Dropdown Events
    // =====================================================

    onDirectieDropdownOpened(index: number): void {
        this.filteredDirectiiByIndex[index] = [...this.directiiList];
        if (this.directiiSearchControls[index]) {
            this.directiiSearchControls[index].setValue('');
        }
    }

    onCompartimentDropdownOpened(index: number): void {
        const row = this.flowData?.redactions[index];
        if (row?.directie_id) {
            this.loadCompartimenteForDirectie(row.directie_id);
            this.filteredCompartimenteByIndex[index] = this.compartimenteByDirectie[row.directie_id] || [];
        }
        if (this.compartimenteSearchControls[index]) {
            this.compartimenteSearchControls[index].setValue('');
        }
    }

    onFunctieDropdownOpened(index: number): void {
        this.filteredFunctiiByIndex[index] = [...this.functiiList];
        if (this.functiiSearchControls[index]) {
            this.functiiSearchControls[index].setValue('');
        }
    }

    onUserDropdownOpened(index: number): void {
        const row = this.flowData?.redactions[index];
        if (row?.directie_id) {
            const compartimentId = row.compartiment_id ?? undefined;
            this.loadUsersForSelection(row.directie_id, compartimentId);
            const cacheKey = compartimentId ? `${row.directie_id}-${compartimentId}` : `${row.directie_id}`;
            this.filteredUsersByIndex[index] = this.usersByDirectieCompartiment[cacheKey] || [];
        }
        if (this.usersSearchControls[index]) {
            this.usersSearchControls[index].setValue('');
        }
    }

    onDropdownClosed(index: number, type: string): void {
        // Reset search control
        const control = type === 'directie' ? this.directiiSearchControls[index] :
                       type === 'compartiment' ? this.compartimenteSearchControls[index] :
                       type === 'functie' ? this.functiiSearchControls[index] :
                       this.usersSearchControls[index];
        if (control) {
            control.setValue('');
        }
    }

    // =====================================================
    // Cascading Selection Changes
    // =====================================================

    onDirectieChange(row: TemplateRedactionNode, index: number): void {
        // Clear compartiment and user when directie changes
        row.compartiment_id = undefined;
        row.compartiment_name = undefined;
        row.user_id = undefined;
        row.user_name = undefined;

        // Load new compartimente
        if (row.directie_id) {
            this.loadCompartimenteForDirectie(row.directie_id);
            this.loadUsersForSelection(row.directie_id);

            // Update directie name
            const directie = this.directiiList.find(d => d.id_directie === row.directie_id);
            row.directie_name = directie?.name;
        }

        // Clear filtered lists
        this.filteredCompartimenteByIndex[index] = [];
        this.filteredUsersByIndex[index] = [];

        this.saveRedaction(row);
    }

    onCompartimentChange(row: TemplateRedactionNode, index: number): void {
        // Clear user when compartiment changes
        row.user_id = undefined;
        row.user_name = undefined;

        // Load new users
        if (row.directie_id) {
            const compartimentId = row.compartiment_id ?? undefined;
            this.loadUsersForSelection(row.directie_id, compartimentId);

            // Update compartiment name
            const compartimente = this.compartimenteByDirectie[row.directie_id] || [];
            const compartiment = compartimente.find(c => c.id_compartiment === row.compartiment_id);
            row.compartiment_name = compartiment?.name;
        }

        // Clear filtered users
        this.filteredUsersByIndex[index] = [];

        this.saveRedaction(row);
    }

    onFunctieChange(row: TemplateRedactionNode, index: number): void {
        // Update functie name
        const functie = this.functiiList.find(f => f.id === row.user_type);
        row.functie_name = functie?.name;

        this.saveRedaction(row);
    }

    onUserChange(row: TemplateRedactionNode, index: number): void {
        // Update user name
        const cacheKey = row.compartiment_id ? `${row.directie_id}-${row.compartiment_id}` : `${row.directie_id}`;
        const users = this.usersByDirectieCompartiment[cacheKey] || [];
        const user = users.find((u: any) => u.id_user === row.user_id);
        row.user_name = user?.name;

        this.saveRedaction(row);
    }

    onRedactionTypeChange(row: TemplateRedactionNode): void {
        this.saveRedaction(row);
    }

    // =====================================================
    // Data Loading
    // =====================================================

    loadCompartimenteForDirectie(directieId: number): void {
        if (this.compartimenteByDirectie[directieId]) {
            return;
        }

        this.documentTypesService
            .getCompartimenteByDirectie(directieId)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (res: any) => {
                    this.compartimenteByDirectie[directieId] = res.data || [];

                    // Update filtered lists for all rows that have this directie selected
                    this.flowData?.redactions.forEach((row, index) => {
                        if (row.directie_id === directieId) {
                            this.filteredCompartimenteByIndex[index] = [...this.compartimenteByDirectie[directieId]];
                        }
                    });
                },
                error: (error) => {
                    console.error('Error loading compartimente:', error);
                    this.compartimenteByDirectie[directieId] = [];
                }
            });
    }

    loadUsersForSelection(directieId: number, compartimentId?: number): void {
        const cacheKey = compartimentId ? `${directieId}-${compartimentId}` : `${directieId}`;

        if (this.usersByDirectieCompartiment[cacheKey]) {
            return;
        }

        this.documentTypesService
            .getAvailableUsersGeneric(directieId, compartimentId)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (res: any) => {
                    this.usersByDirectieCompartiment[cacheKey] = res.data || [];

                    // Update filtered lists for all rows that match this directie + compartiment combination
                    this.flowData?.redactions.forEach((row, index) => {
                        const rowCompartimentId = row.compartiment_id ?? undefined;
                        const matches = row.directie_id === directieId &&
                                      (compartimentId === rowCompartimentId ||
                                       (!compartimentId && !rowCompartimentId));

                        if (matches) {
                            this.filteredUsersByIndex[index] = [...this.usersByDirectieCompartiment[cacheKey]];
                        }
                    });
                },
                error: (error) => {
                    console.error('Error loading users:', error);
                    this.usersByDirectieCompartiment[cacheKey] = [];
                }
            });
    }

    // =====================================================
    // CRUD Operations
    // =====================================================

    saveRedaction(row: TemplateRedactionNode): void {
        if (!row.id || this.isSaving) return;

        this.isSaving = true;

        this.templateService.updateTemplateRedaction(this.data.id, row.id, {
            redaction_type: row.redaction_type,
            description: row.description,
            user_type: row.user_type,
            directie_id: row.directie_id,
            compartiment_id: row.compartiment_id,
            user_id: row.user_id
        }).subscribe({
            next: () => {
                this.isSaving = false;
            },
            error: (error) => {
                console.error('Error saving redaction:', error);
                this.isSaving = false;
            }
        });
    }

    // Drag and drop pentru reordonare
    onDrop(event: CdkDragDrop<TemplateRedactionNode[]>): void {
        if (!this.canManage || !this.flowData) return;

        if (event.previousIndex === event.currentIndex) return;

        moveItemInArray(this.flowData.redactions, event.previousIndex, event.currentIndex);

        // Recalculate orders
        this.flowData.redactions.forEach((item, index) => {
            item.order = index + 1;
        });

        this.saveOrder();
    }

    saveOrder(): void {
        if (!this.flowData) return;

        this.isSaving = true;
        const orderData = this.flowData.redactions.map(r => ({ id: r.id, order: r.order }));

        this.templateService.reorderTemplateRedactions(this.data.id, orderData).subscribe({
            next: () => {
                this.isSaving = false;
            },
            error: (error) => {
                console.error('Error saving order:', error);
                this.isSaving = false;
                this.loadInitialData(); // Reload to reset
            }
        });
    }

    addRedaction(): void {
        if (!this.canManage) return;

        Swal.fire({
            title: 'Adauga pas redactare',
            html: `
                <div class="form-group mb-3">
                    <label>Tip redactare *</label>
                    <select id="swal-redaction-type" class="form-control">
                        <option value="1">Editare + Upload</option>
                        <option value="2">Review/Aprobare</option>
                    </select>
                </div>
                <div class="form-group mb-3">
                    <label>Descriere (optional)</label>
                    <input type="text" id="swal-description" class="form-control" placeholder="Descriere pas...">
                </div>
            `,
            showCancelButton: true,
            confirmButtonText: 'Adauga',
            cancelButtonText: 'Anuleaza',
            confirmButtonColor: '#f59e0b',
            preConfirm: () => {
                const redactionType = (document.getElementById('swal-redaction-type') as HTMLSelectElement).value;
                const description = (document.getElementById('swal-description') as HTMLInputElement).value;
                return { redaction_type: parseInt(redactionType), description };
            }
        }).then((result) => {
            if (result.isConfirmed && result.value) {
                this.templateService.addTemplateRedaction(this.data.id, result.value).subscribe({
                    next: () => {
                        Swal.fire('Succes!', 'Pasul a fost adaugat.', 'success');
                        this.loadInitialData();
                    },
                    error: (error) => {
                        Swal.fire('Eroare', error.error?.message || 'Nu s-a putut adauga pasul.', 'error');
                    }
                });
            }
        });
    }

    deleteRedaction(redaction: TemplateRedactionNode): void {
        if (!this.canManage) return;

        Swal.fire({
            title: 'Sterge pas redactare',
            text: `Sigur doriti sa stergeti pasul #${redaction.order}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sterge',
            cancelButtonText: 'Anuleaza',
            confirmButtonColor: '#dc3545'
        }).then((result) => {
            if (result.isConfirmed) {
                this.templateService.deleteTemplateRedaction(this.data.id, redaction.id).subscribe({
                    next: () => {
                        Swal.fire('Sters!', 'Pasul a fost sters.', 'success');
                        this.loadInitialData();
                    },
                    error: (error) => {
                        Swal.fire('Eroare', error.error?.message || 'Nu s-a putut sterge pasul.', 'error');
                    }
                });
            }
        });
    }

    // =====================================================
    // Helper Methods
    // =====================================================

    getTypeIcon(type: number): string {
        return this.redactionTypes[type]?.icon || 'edit_document';
    }

    getTypeLabel(type: number): string {
        return this.redactionTypes[type]?.label || 'Editare';
    }

    getTypeClass(type: number): string {
        return this.redactionTypes[type]?.class || 'redaction-type-edit';
    }

    getDirectieName(directieId: number | undefined): string {
        if (!directieId) return '';
        const directie = this.directiiList.find(d => d.id_directie === directieId);
        return directie?.name || '';
    }

    getCompartimentName(directieId: number | undefined, compartimentId: number | undefined): string {
        if (!directieId || !compartimentId) return '';
        const compartimente = this.compartimenteByDirectie[directieId] || [];
        const compartiment = compartimente.find(c => c.id_compartiment === compartimentId);
        return compartiment?.name || '';
    }

    getFunctieName(userType: number | undefined): string {
        if (!userType) return '';
        const functie = this.functiiList.find(f => f.id === userType);
        return functie?.name || '';
    }
}
